/**
 * Check if the file size is within the specified maximum size.
 *
 * Input format is "<number> <unit>" where unit is one of "B", "KB", "MB", "GB".
 * If a number is provided, it is treated as bytes.
 *
 * Examples of valid maxSize inputs:
 * - "500 KB"
 * - "2 MB"
 * - 1048576 (which is 1 MB in bytes)
 *
 * @param fileSize - The size of the file in bytes.
 * @param maxSize - The maximum allowed size as a string with unit or a number in bytes.
 * @returns True if the file size is within the limit, false otherwise.
 */
export const checkFileSize = (
  fileSize: number,
  maxSize: number | string,
): boolean => {
  if (typeof maxSize === 'number') {
    return fileSize <= maxSize;
  }

  const sizePattern = /^(\d+(?:\.\d+)?)\s+(B|KB|MB|GB)$/i;
  const match = sizePattern.exec(maxSize.trim());

  if (!match) {
    throw new Error(
      'Invalid maxSize format. Use "<number> <unit>" where unit is B, KB, MB, or GB.',
    );
  }

  const [, rawSizeValue, rawSizeUnit] = match;
  if (!rawSizeValue || !rawSizeUnit) {
    throw new Error('Invalid maxSize format');
  }
  const sizeValue = Number(rawSizeValue);
  const sizeUnit = rawSizeUnit.toUpperCase();

  const unitMultipliers: Record<string, number> = {
    B: 1,
    GB: 1024 * 1024 * 1024,
    KB: 1024,
    MB: 1024 * 1024,
  };

  const multiplier = unitMultipliers[sizeUnit];
  if (!multiplier) {
    throw new Error('Invalid maxSize unit');
  }
  const maxSizeInBytes = sizeValue * multiplier;

  return fileSize <= maxSizeInBytes;
};
